// src/components/ScheduleSurgeryModal.js
// Author: Sunil Jain & Gemini
// MODIFIED: This version corrects a bug that prevented editing/saving surgery dates.

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, CircularProgress, Alert, IconButton, Stack, Box,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';
import { format, parseISO, addDays, set } from 'date-fns';

const ScheduleSurgeryModal = ({ open, onClose, episodes = [], patientUserId, tenantId }) => { 
    const queryClient = useQueryClient();
    const patientDetailQueryKey = (pid) => ['patient', pid, 'detailV8'];
    const [selectedEpisodeId, setSelectedEpisodeId] = React.useState(''); 
    
    const [surgeryDateTime, setSurgeryDateTime] = React.useState('');
    const [error, setError] = React.useState('');

    // This effect runs ONCE when the modal opens to set the initial state.
    React.useEffect(() => {
        if (open && episodes.length > 0) {
            setError('');

            const episodeWithDate = episodes.find(ep => ep.surgery_datetime);
            const initialEpisodeId = episodeWithDate ? episodeWithDate.id : episodes[0].id;
            
            setSelectedEpisodeId(initialEpisodeId);

            const episodeToDisplay = episodes.find(ep => ep.id === initialEpisodeId);
            const initialDateTime = episodeToDisplay?.surgery_datetime
                ? format(parseISO(episodeToDisplay.surgery_datetime), "yyyy-MM-dd'T'HH:mm")
                : (() => {
                    const tomorrow = addDays(new Date(), 1);
                    const tomorrowAt8AM = set(tomorrow, { hours: 8, minutes: 0, seconds: 0, milliseconds: 0 });
                    return format(tomorrowAt8AM, "yyyy-MM-dd'T'HH:mm");
                })();

            setSurgeryDateTime(initialDateTime);
        }
    }, [open, episodes]);

    // This effect runs when the user SWITCHES episodes in the dropdown.
    React.useEffect(() => {
        if (selectedEpisodeId) {
            const selectedEp = episodes.find(ep => ep.id === selectedEpisodeId);

            // --- START OF FIX 1 ---
            // This logic now correctly provides the default for any unscheduled episode,
            // preventing the date from disappearing when the user switches.
            const dateTimeForSelectedEp = selectedEp?.surgery_datetime
                ? format(parseISO(selectedEp.surgery_datetime), "yyyy-MM-dd'T'HH:mm")
                : (() => {
                    const tomorrow = addDays(new Date(), 1);
                    const tomorrowAt8AM = set(tomorrow, { hours: 8, minutes: 0, seconds: 0, milliseconds: 0 });
                    return format(tomorrowAt8AM, "yyyy-MM-dd'T'HH:mm");
                })();
            setSurgeryDateTime(dateTimeForSelectedEp);
        }
    }, [selectedEpisodeId, episodes]);

    const mutation = useMutation({
        mutationFn: ({ episodeId, dateTimePayload }) => authFetch(`/api/episodes/${episodeId}/surgery-datetime`, {
            method: 'PATCH',
            body: { surgery_datetime: dateTimePayload },
        }),
        onSuccess: (updatedEpisode) => {
            logger.info(`Successfully updated surgery date for episode ${updatedEpisode.id}`);
            
            const patientEpisodesQueryKey = ['patient', patientUserId, 'episodesV8'];
            queryClient.setQueryData(patientEpisodesQueryKey, (oldData) => {
                if (!oldData) return [updatedEpisode];
                return oldData
                    .filter(ep => ep.status !== 'archived')
                    .map(ep => ep.id === updatedEpisode.id ? updatedEpisode : ep);
            });

            queryClient.invalidateQueries({ queryKey: ['dashboardStats', tenantId] });
            queryClient.invalidateQueries({ queryKey: patientDetailQueryKey(patientUserId) });

            onClose();
        },
        onError: (err) => {
            logger.error("Failed to update surgery date:", err);
            setError(err.data?.detail || err.message || "An unexpected error occurred.");
        },
    });

    const handleSave = () => {
        if (!surgeryDateTime) {
            setError("Please select a surgery date and time.");
            return;
        }
        const isoDateTime = new Date(surgeryDateTime).toISOString();
        mutation.mutate({ episodeId: selectedEpisodeId, dateTimePayload: isoDateTime });
    };

    const handleCancelSurgery = () => {
        mutation.mutate({ episodeId: selectedEpisodeId, dateTimePayload: null });
    };
    
    // --- START OF FIX 2 ---
    // The complex change handler has been removed. A simple one is more reliable
    // because the default value is now correctly set by the useEffect hooks above.
    const handleDateTimeChange = (event) => {
        setSurgeryDateTime(event.target.value);
    };
    // --- END OF FIX 2 ---
    
    const selectedEpisodeHasDate = episodes.find(ep => ep.id === selectedEpisodeId)?.surgery_datetime;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {selectedEpisodeHasDate ? 'Reschedule Surgery' : 'Schedule Surgery'}
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                <FormControl fullWidth margin="normal">
                    <InputLabel id="episode-select-label">For Episode</InputLabel>
                    <Select
                        labelId="episode-select-label"
                        value={selectedEpisodeId}
                        label="For Episode"
                        onChange={(e) => setSelectedEpisodeId(e.target.value)}
                    >
                        {episodes.map((ep) => (
                            <MenuItem key={ep.id} value={ep.id}>
                                {ep.program?.name || ep.description || `Episode starting ${ep.start_date}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    autoFocus
                    fullWidth
                    margin="normal"
                    label="Surgery Date & Time"
                    type="datetime-local"
                    value={surgeryDateTime}
                    onChange={handleDateTimeChange} // Uses the corrected, simple handler
                    InputLabelProps={{ shrink: true }}
                    disabled={!selectedEpisodeId}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" width="100%">
                    {selectedEpisodeHasDate ? (
                         <Button onClick={handleCancelSurgery} color="error" disabled={mutation.isPending}>
                            Cancel Surgery
                        </Button>
                    ) : <Box />}
                    <Stack direction="row" spacing={1}>
                        <Button onClick={onClose} disabled={mutation.isPending}>Close</Button>
                        <Button 
                            onClick={handleSave} 
                            variant="contained" 
                            disabled={mutation.isPending || !selectedEpisodeId}
                        >
                            {mutation.isPending ? <CircularProgress size={24} /> : 'Save Date'}
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleSurgeryModal;